import React, { memo } from 'react';
import { ScrollView, View } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import {
  PreviewUserInfo,
  PreviewResumeBio,
  PreviewResumeJobStatus,
  PreviewResumeEducationItem,
  PreviewResumeExperienceItem,
  PreviewResumeSkillItem,
  PreviewResumeAchievementItem,
  PreviewResumeQR,
} from '@redlink/components/molecules';
import {
  PreviewResumeItemsGroup,
  PreviewResumeContactInfo,
  PreviewResumeLinks,
} from '@redlink/components/muscles';
import { PreviewResumeProjects } from '@redlink/components/organisms';
import { useLocale } from '@redlink/assets/locale';
import { useQuery, getPagePreviewResumeQuery } from '@redlink/services';
import { LoadingContainer, Space } from '@redlink/components/atoms';

export const PreviewResumePage = memo(({ pageName }) => {
  const { styles } = useStyle();
  const { dictionary } = useLocale();

  const {
    loading,
    data: {
      ResumePreview: {
        contacts: {
          bio,
          jobStatus,
          emailAddress,
          cellphoneNumber,
          address,
        } = {},
        educations = [],
        experiences = [],
        skills = [],
        projects = [],
        achievements = [],
        links = [],
        qrCodeUrl,
      } = {},
    },
  } = useQuery(getPagePreviewResumeQuery, { variables: { pageName } });

  const hasBio = !!bio;
  const hasQrCodeUrl = !!qrCodeUrl;
  const jobStatusShouldBeShown = jobStatus === 'openToWork';
  const contactsShouldBeShown = cellphoneNumber || address || emailAddress;
  const hasEducation = educations.length > 0;
  const hasExperiences = experiences.length > 0;
  const hasSkills = skills.length > 0;
  const hasProjects = projects.length > 0;
  const hasLinks = links.length > 0;
  const hasAchievements = achievements.length > 0;

  return (
    <LoadingContainer
      hasOverlay={false}
      isBusy={loading}
      style={styles.container}
    >
      <PreviewUserInfo {...{ pageName }} style={styles.userInfoContainer} />
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.blocksContainer}>
          {jobStatusShouldBeShown && (
            <>
              <PreviewResumeJobStatus />
              <Space size={24} />
            </>
          )}
          {hasBio && (
            <>
              <PreviewResumeBio title={dictionary.bio} data={bio} />
              <Space size={24} />
            </>
          )}
          {hasEducation && (
            <>
              <PreviewResumeItemsGroup
                title={dictionary.education}
                data={educations}
                ItemComponent={(props) => (
                  <PreviewResumeEducationItem {...props} />
                )}
              />
              <Space size={24} />
            </>
          )}
          {hasExperiences && (
            <>
              <PreviewResumeItemsGroup
                title={dictionary.experience}
                data={experiences}
                ItemComponent={(props) => (
                  <PreviewResumeExperienceItem {...props} />
                )}
              />
              <Space size={24} />
            </>
          )}
          {hasSkills && (
            <>
              <PreviewResumeItemsGroup
                title={dictionary.skills}
                data={skills}
                ItemComponent={(props) => <PreviewResumeSkillItem {...props} />}
                limit={3}
              />
              <Space size={24} />
            </>
          )}
        </View>
        {hasProjects && (
          <>
            <PreviewResumeProjects
              title={dictionary.featuredProjects}
              data={projects}
              {...{ pageName }}
            />
            <Space size={24} />
          </>
        )}
        <View style={styles.blocksContainer}>
          {hasAchievements && (
            <>
              <PreviewResumeItemsGroup
                title={dictionary.achievements}
                data={achievements}
                ItemComponent={(props) => (
                  <PreviewResumeAchievementItem {...props} />
                )}
              />
              <Space size={24} />
            </>
          )}
          {contactsShouldBeShown && (
            <>
              <PreviewResumeContactInfo
                title={dictionary.contactInfo}
                data={{ emailAddress, address, cellphoneNumber }}
              />
              <Space size={24} />
            </>
          )}
          {hasLinks && (
            <PreviewResumeLinks title={dictionary.socialMedia} data={links} />
          )}
          <Space size={32} />
          {hasQrCodeUrl && <PreviewResumeQR qrCode={qrCodeUrl} />}
          <Space size={32} />
        </View>
      </ScrollView>
    </LoadingContainer>
  );
});

const useStyle = createUseStyle(({ colors }) => ({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContent: {
    paddingVertical: 16,
  },
  userInfoContainer: {
    margin: 24,
  },
  blocksContainer: {
    paddingHorizontal: 24,
  },
}));
